import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

// Add this to your existing header.js file

/**
 * Search form functionality for inline search forms
 */
class SearchFormComponent {
  constructor() {
    this.init();
  }

  init() {
    this.setupSearchForms();
    this.setupPredictiveSearch();
  }

  setupSearchForms() {
    const searchForms = document.querySelectorAll('[data-search-form]');
    
    searchForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[type="search"]');
        if (!input.value.trim()) {
          e.preventDefault();
          input.focus();
        }
      });
    });
  }

  setupPredictiveSearch() {
    if (!window.Shopify?.routes?.predictive_search_url) return;
    
    const searchInputs = document.querySelectorAll('[data-predictive-search-input]');
    
    searchInputs.forEach(input => {
      let timeout;
      
      input.addEventListener('input', (e) => {
        clearTimeout(timeout);
        const query = e.target.value.trim();
        
        if (query.length < 2) {
          this.hidePredictiveSearch(input);
          return;
        }
        
        timeout = setTimeout(() => {
          this.performPredictiveSearch(input, query);
        }, 300);
      });

      input.addEventListener('blur', () => {
        setTimeout(() => {
          this.hidePredictiveSearch(input);
        }, 200);
      });

      input.addEventListener('focus', () => {
        const query = input.value.trim();
        if (query.length >= 2) {
          this.performPredictiveSearch(input, query);
        }
      });
    });
  }

  async performPredictiveSearch(input, query) {
    const form = input.closest('[data-search-form]');
    const predictiveContainer = form?.querySelector('[data-predictive-search]');
    
    if (!predictiveContainer) return;

    try {
      this.showLoadingState(predictiveContainer);
      
      const url = `${window.Shopify.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=6`;
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Search request failed');
      
      const data = await response.json();
      this.renderResults(predictiveContainer, data, query);
      
    } catch (error) {
      console.error('Predictive search error:', error);
      this.hidePredictiveSearch(input);
    }
  }

  showLoadingState(container) {
    container.style.display = 'block';
    container.setAttribute('data-predictive-search-live-region-count-value', 'loading');
    container.innerHTML = `
      <div class="predictive-search__loading-state" style="padding: 12px; text-align: center; color: var(--color-foreground); opacity: 0.7;">
        <div style="display: inline-flex; align-items: center; justify-content: center;">
          <svg class="spinner" width="20" height="20" viewBox="0 0 50 50" style="animation: rotate 2s linear infinite;">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" style="stroke-linecap: round; animation: dash 1.5s ease-in-out infinite;"></circle>
          </svg>
        </div>
        <span style="margin-left: 8px;">Searching...</span>
      </div>
    `;
  }

  renderResults(container, data, query) {
    const products = data.resources?.results?.products || [];
    const collections = data.resources?.results?.collections || [];
    
    if (products.length === 0 && collections.length === 0) {
      this.hidePredictiveSearch();
      return;
    }

    let html = '<div class="predictive-search__results" style="padding: 12px;">';
    
    // Products
    if (products.length > 0) {
      html += '<div class="predictive-search__products">';
      html += `<h3 style="font-size: 12px; font-weight: bold; margin: 0 0 8px 0; color: var(--color-foreground); text-transform: uppercase; letter-spacing: 0.5px;">Products</h3>`;
      
      products.slice(0, 4).forEach(product => {
        const imageHtml = product.image 
          ? `<img src="${product.image}" alt="${product.title}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;">` 
          : '';
        
        html += `
          <a href="${product.url}" class="predictive-search__item" style="display: flex; align-items: center; padding: 8px; text-decoration: none; color: var(--color-foreground); border-radius: 4px; margin-bottom: 4px; transition: background-color 0.2s ease;" onmouseover="this.style.backgroundColor='rgba(var(--color-foreground-rgb), 0.05)'" onmouseout="this.style.backgroundColor='transparent'">
            ${imageHtml}
            <div style="margin-left: ${product.image ? '12px' : '0'};">
              <div style="font-weight: 500; font-size: 14px; line-height: 1.3;">${product.title}</div>
              ${product.price ? `<div style="font-size: 12px; color: var(--color-foreground); opacity: 0.7; margin-top: 2px;">${product.price}</div>` : ''}
            </div>
          </a>
        `;
      });
      
      html += '</div>';
    }
    
    // Collections
    if (collections.length > 0) {
      html += `<div class="predictive-search__collections" style="margin-top: 16px;">`;
      html += `<h3 style="font-size: 12px; font-weight: bold; margin: 0 0 8px 0; color: var(--color-foreground); text-transform: uppercase; letter-spacing: 0.5px;">Collections</h3>`;
      
      collections.slice(0, 3).forEach(collection => {
        const imageHtml = collection.image 
          ? `<img src="${collection.image}" alt="${collection.title}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;">` 
          : '';
        
        html += `
          <a href="${collection.url}" class="predictive-search__item" style="display: flex; align-items: center; padding: 8px; text-decoration: none; color: var(--color-foreground); border-radius: 4px; margin-bottom: 4px; transition: background-color 0.2s ease;" onmouseover="this.style.backgroundColor='rgba(var(--color-foreground-rgb), 0.05)'" onmouseout="this.style.backgroundColor='transparent'">
            ${imageHtml}
            <div style="margin-left: ${collection.image ? '12px' : '0'};">
              <div style="font-weight: 500; font-size: 14px; line-height: 1.3;">${collection.title}</div>
            </div>
          </a>
        `;
      });
      
      html += '</div>';
    }

    // View all results
    html += `
      <div style="border-top: 1px solid var(--color-border); margin-top: 12px; padding-top: 12px;">
        <a href="/search?q=${encodeURIComponent(query)}" style="display: block; text-align: center; padding: 8px; color: var(--color-foreground); text-decoration: none; font-weight: 500; border-radius: 4px; font-size: 14px; transition: background-color 0.2s ease;" onmouseover="this.style.backgroundColor='rgba(var(--color-foreground-rgb), 0.05)'" onmouseout="this.style.backgroundColor='transparent'">
          View all results for "${query}"
        </a>
      </div>
    `;
    
    html += '</div>';
    
    container.innerHTML = html;
    container.style.display = 'block';
    container.setAttribute('data-predictive-search-live-region-count-value', products.length + collections.length);
  }

  hidePredictiveSearch(input = null) {
    const containers = input 
      ? [input.closest('[data-search-form]')?.querySelector('[data-predictive-search]')]
      : document.querySelectorAll('[data-predictive-search]');
    
    containers.forEach(container => {
      if (container) {
        container.style.display = 'none';
        container.removeAttribute('data-predictive-search-live-region-count-value');
      }
    });
  }
}

// Initialize search form functionality when DOM is ready
onDocumentLoaded(() => {
  // Initialize search forms
  new SearchFormComponent();
  
  // Keyboard navigation for predictive search
  document.addEventListener('keydown', (e) => {
    const activeElement = document.activeElement;
    const form = activeElement?.closest('[data-search-form]');
    const predictiveContainer = form?.querySelector('[data-predictive-search]');
    
    if (!predictiveContainer || predictiveContainer.style.display === 'none') return;
    
    const searchItems = predictiveContainer.querySelectorAll('.predictive-search__item');
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentIndex = Array.from(searchItems).indexOf(activeElement);
      const nextIndex = currentIndex < searchItems.length - 1 ? currentIndex + 1 : 0;
      searchItems[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = Array.from(searchItems).indexOf(activeElement);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : searchItems.length - 1;
      searchItems[prevIndex]?.focus();
    } else if (e.key === 'Escape') {
      const searchInput = form?.querySelector('input[type="search"]');
      if (searchInput) {
        searchInput.focus();
        new SearchFormComponent().hidePredictiveSearch();
      }
    }
  });
});

// Add CSS animations for search spinner
const style = document.createElement('style');
style.textContent = `
  @keyframes rotate {
    100% { transform: rotate(360deg); }
  }
  @keyframes dash {
    0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
    50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
    100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
  }
`;
document.head.appendChild(style);